import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { colorGrid } from 's2s-themes';
import styled from 'styled-components';
import RangeSlider from 's2s-range-slider';
import Button from 's2s-button';
import { getIntlText } from '../js/utilities';
import Select from 's2s-select';
import AppService from 's2s-app-service';

const AudioControlsContainer = styled.div`
  background-color: ${colorGrid.gray0};
  display: flex;
  flex-direction: column;
  width : 100%;
`;

const AudioSettingsContainer = styled.div`
  border-bottom: 1px solid ${colorGrid.gray3};
  display: flex;
  flex-direction: column;
  font-family: Open Sans;
  font-size: 14px;
  justify-content: space-between;
  padding: 16px 20px 32px 20px;
`;

const Heading = styled.div`
  font-weight: 600;
`;

const Controls = styled.div`
  .RangeContainer {
    border: 1px solid #cccfd2;
  }
  
  .RangeSlider {
    height: 40px;
  }
  
  .react-select__control {
    border: 1px solid #cccfd2;
    box-shadow: none;
    
    :hover {
      border: 1px solid #243991;
    }
  }
  
  .react-select__control--is-focused {
    border: 1px solid #243991;
  }
  
  .react-select__dropdown-indicator {
    color: rgba(0, 0, 0, 0.6);
  }
  
  .react-select__indicator-separator {
    visibility: hidden;
  }
  
  .react-select__option--is-focused {
    background-color: rgba(36, 57, 145, 0.08);
    color: #243991;
  }
  
  .react-select__option--is-selected {
    background-color: rgba(36, 57, 145, 0.08);
    color: #243991;
  }
  
  .react-select__single-value {
    color: #212121;
  }
  
  .Slider {
    background-color: #e5e6e8;
    height: 8px;
    margin-top: 16px;
  }
  
  .SliderControl {
    background-color: #243991;
    top: -6px;
  }
  
  .SliderFill {
    background-color: #243991;
    height: 100%;
  }
  
  .SVGContainer {
    margin-left: 8px;
  }
`;

const StyledSelect = styled(Select)`
  height: 46px;
  margin-bottom: 16px;
  margin-right: 0px;
`;

const AudioControlsFooter = styled.div`
  display: flex;
  padding: 16px;
`;

const CancelButton = styled(Button)`
  background-color: ${colorGrid.gray0};
  border: 1px solid #a7b4e9;
  color: #243991;
  display: inline-flex;
  font-family: Open Sans;
  font-weight: 700;
  justify-content: center;
  letter-spacing: 1.25px;
  margin-right: 16px;
  text-transform: uppercase;
  width: 50%;

  &:hover {
    background-color: #d3d9f4;
  }
`;

const SaveButton = styled(Button)`
  background-color: #099f24;
  display: inline-flex;
  font-weight: 700;
  justify-content: center;
  letter-spacing: 1.25px;
  text-transform: uppercase;
  width: 50%;
  
  &:hover {
    background-color: #066a18;
  }
`;

const AudioControls = (props) => {
  const callVolume = AppService.getInstance().getCallService().getVolume(props.activeCallId);

  const [outputValueState, setOutputValueState] = useState(callVolume * 100);
  const [selectedOutputDeviceState, setSelectedOutputDeviceState] = useState(props.selectedOutputDevice);
  const [outputAudio, setOutputAudio] = useState([]);

  const getAudio = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();

    const outputAudio = devices.filter((device) => {
      return device.kind === "audiooutput"; // returns a MediaDeviceInfo object which is read-only
    }).map((device) => {
      // MediaDeviceInfo is a read only object so I must create a new object so it can be successfully merged later on change
      return {
        "deviceId": device.deviceId,
        "groupId": device.groupId,
        "kind": device.kind,
        "label": device.label
      };
    });

    setOutputAudio(outputAudio);
  };

  useEffect(() => {
    getAudio();
  }, []); // initial render

  const handleOutputDeviceChange = (device) => {
    setSelectedOutputDeviceState(device);
  };

  const handleOutputVolumeChange = (volume) => {
    setOutputValueState(volume);
  };

  const saveSettings = () => {
    // Object expected for updating Settings hook
    const updatedSettings = {
      selectedSpeaker: selectedOutputDeviceState
    };

    // Object expected by CallService
    const callServiceSettings = {
      outputDevice: {
        id: selectedOutputDeviceState.value
      },
      outputVolume: outputValueState / 100,
    };

    AppService.getInstance().getCallService().setAudio(props.activeCallId, callServiceSettings); // setting selected speaker

    // cbOnClick just signals to parent to close AudioControls
    // and passes updatedSettings object so app can accurately keep track of the selected speaker.
    props.cbOnClick('audioSave', updatedSettings);
  };

  const cancelOnClick = () => {
    props.cbOnClick('cancel');
  };

  // Returns an array of output device objects that works with s2s-select
  const outputDevices = outputAudio && outputAudio.map((device) => {
    return {
      "value": device.deviceId,
      "label": device.label
    };
  });

  const defaultOutputDevice = outputDevices && outputDevices.length > 0 && outputDevices[0];

  return (
    <AudioControlsContainer className="AudioControlsContainer">
      <AudioSettingsContainer className="AudioSettingContainer">
        <Heading className="Heading" >
          {getIntlText(props.intl, 'AUDIOCONTROLS-OUTPUT-HEADING', 'Output')}
        </Heading>
        <Controls className="Controls">
          <StyledSelect
            className="SelectComponent"
            cbOnValueChange={handleOutputDeviceChange}
            menuItems={outputDevices}
            maxHeight="110px"
            selectedItem={selectedOutputDeviceState.value ? selectedOutputDeviceState.value : defaultOutputDevice.value}
          />
          <RangeSlider
            cbOnChange={handleOutputVolumeChange}
            className="OutputSlider"
            initialSliderPositionValue={outputValueState}
            svgName={outputValueState ? 'MdVolumeUp' : 'MdVolumeMute'}
            svgPosition="left"
          />
        </Controls>
      </AudioSettingsContainer>
      <AudioControlsFooter className="AudioControlsFooter">
        <CancelButton
          className="CancelButton"
          ariaLabel={getIntlText(props.intl, 'ARIA-CANCEL-BTN', "Cancel button")}
          buttonLabel={getIntlText(props.intl, 'AUDIOCONTROLS-CANCEL-BTN-LABEL', 'Cancel')}
          buttonSize="large"
          buttonType="secondary"
          cbClick={cancelOnClick}
        />
        <SaveButton
          className="SaveButton"
          ariaLabel={getIntlText(props.intl, 'ARIA-SAVE-BTN', "Save button")}
          buttonLabel={getIntlText(props.intl, 'AUDIOCONTROLS-SAVE-BTN-LABEL', 'Save')}
          buttonSize="large"
          buttonType="primaryGreen"
          cbClick={saveSettings}
        />
      </AudioControlsFooter>
    </AudioControlsContainer>
  );
};

AudioControls.propTypes = {
  activeCallId: PropTypes.string,
  cbOnClick: PropTypes.func, //returns cancel or an object with volume and device settings
  cbRefreshDevices: PropTypes.func,
  outputs: PropTypes.shape({
    value: PropTypes.number, //volume
    array: PropTypes.array //array of devices
  }),
  selectedOutputDevice: PropTypes.object
};

AudioControls.defaultProps = {
  activeCallId: undefined,
  cbOnClick: () => { console.log('cbOnClick is not defined in s2s-audio-controls'); },
  cbRefreshDevices: () => { console.log('cbRefreshDevices is not defined in s2s-audio-controls'); },
  outputs: {
    value: 1,
    array: [{ "value": "no devices", "label": "No Devices" }]
  },
  selectedOutputDevice: { "value": "no devices", "label": "No Devices" }
};

export default AudioControls;
